import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { cn } from "@/lib/utils";

/**
 * Option interface for FilterSelect component
 */
export interface FilterSelectOption {
  /** Option value */
  value: string;
  /** Option display label */
  label: string;
  /** Whether option is disabled */
  disabled?: boolean;
}

/**
 * Props for the FilterSelect component
 */
interface FilterSelectProps {
  /** Array of select options */
  options: FilterSelectOption[];
  /** Selected value */
  value?: string;
  /** Callback fired when value changes */
  onChange?: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Whether select is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * FilterSelect component displays a single-select dropdown for filter values.
 * Built on Radix UI Select primitive with custom styling for filter contexts.
 * 
 * @component
 * @param {FilterSelectProps} props - Component props
 * @param {FilterSelectOption[]} props.options - Select options
 * @param {string} [props.value] - Selected value
 * @param {(value: string) => void} [props.onChange] - Change callback
 * @param {string} [props.placeholder] - Placeholder text
 * @param {boolean} [props.disabled] - Whether disabled
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} Single-select dropdown
 * 
 * @example
 * ```tsx
 * <FilterSelect
 *   options={options}
 *   value={selectedValue}
 *   onChange={handleChange}
 *   placeholder="Select a value"
 * />
 * ```
 */
export const FilterSelect: React.FC<FilterSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  className,
}) => {
  return (
    <Select value={value} onValueChange={onChange} disabled={disabled || options.length <= 0}>
      <SelectTrigger
        className={cn(
          "h-9 w-full border border-gray-200 bg-white text-gray-700",
          "focus:ring-1 focus:ring-primary-500 focus:border-primary-500",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

